import { useState, useEffect } from 'react'
import { Container, Row, Col, Card, Table, Button, Badge, Spinner, Alert } from 'react-bootstrap'
import client from '../../api/client'
import GeminiAccountForm from './GeminiAccountForm'
import DeviceCodeAuthModal from '../../components/DeviceCodeAuthModal'

function GeminiAccounts() {
  const [accounts, setAccounts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [editingAccount, setEditingAccount] = useState(null)
  const [testingAccount, setTestingAccount] = useState(null)
  const [refreshingModels, setRefreshingModels] = useState(null)
  const [authAccount, setAuthAccount] = useState(null)
  const [formData, setFormData] = useState({
    email: '',
    label: '',
    tier: 'free',
    daily_request_limit: 1000,
    minute_request_limit: 60,
    is_active: true
  })

  useEffect(() => {
    fetchAccounts()
  }, [])

  const fetchAccounts = async () => {
    setLoading(true)
    try {
      const response = await client.get('/admin/api/v1/gemini-accounts/')
      setAccounts(response.data || [])
    } catch (err) {
      console.error('Error fetching Gemini accounts:', err)
      setError('Erreur lors du chargement des comptes Gemini')
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = () => {
    setEditingAccount(null)
    setFormData({
      email: '',
      label: '',
      tier: 'free',
      daily_request_limit: 1000,
      minute_request_limit: 60,
      is_active: true
    })
    setShowForm(true)
  }

  const handleEdit = (account) => {
    setEditingAccount(account)
    setFormData({
      email: account.email,
      label: account.label,
      tier: account.tier,
      daily_request_limit: account.daily_request_limit,
      minute_request_limit: account.minute_request_limit,
      is_active: account.is_active
    })
    setShowForm(true)
  }

  const handleDelete = async (account) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer le compte "${account.email}" ?`)) {
      return
    }

    try {
      await client.delete(`/admin/api/v1/gemini-accounts/${account.id}`)
      setSuccess('Compte supprimé avec succès')
      fetchAccounts()
    } catch (err) {
      console.error('Error deleting account:', err)
      setError('Erreur lors de la suppression du compte')
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setSuccess('')

    try {
      if (editingAccount) {
        await client.patch(`/admin/api/v1/gemini-accounts/${editingAccount.id}`, formData)
        setSuccess('Compte mis à jour avec succès')
      } else {
        await client.post('/admin/api/v1/gemini-accounts/', formData)
        setSuccess('Compte créé avec succès')
      }
      setShowForm(false)
      fetchAccounts()
    } catch (err) {
      console.error('Error saving account:', err)
      setError(err.response?.data?.detail || 'Erreur lors de l\'enregistrement du compte')
    }
  }

  const handleTestConnection = async (account) => {
    setTestingAccount(account.id)
    setError('')
    setSuccess('')

    try {
      const resp = await client.post(`/admin/api/v1/gemini-accounts/${account.id}/test`)
      if (resp.data.success) {
        setSuccess(`Connexion testée avec succès pour ${account.email}`)
      } else {
        setError(resp.data.message || `Échec du test de connexion pour ${account.email}`)
      }
    } catch (err) {
      console.error('Error testing connection:', err)
      setError(err.response?.data?.detail || `Échec du test de connexion pour ${account.email}`)
    } finally {
      setTestingAccount(null)
    }
  }

  const handleRefreshModels = async (account) => {
    setRefreshingModels(account.id)
    setError('')
    setSuccess('')

    try {
      await client.post(`/admin/api/v1/gemini-accounts/${account.id}/refresh-models`)
      setSuccess(`Modèles rafraîchis avec succès pour ${account.email}`)
      fetchAccounts()
    } catch (err) {
      console.error('Error refreshing models:', err)
      setError(err.response?.data?.detail || `Échec du rafraîchissement des modèles pour ${account.email}`)
    } finally {
      setRefreshingModels(null)
    }
  }

  const getTierBadge = (tier) => {
    const badges = {
      free: 'secondary',
      tier1: 'primary',
      tier2: 'info',
      tier3: 'warning'
    }
    return <Badge bg={badges[tier] || 'secondary'}>{tier.toUpperCase()}</Badge>
  }

  return (
    <Container fluid className="py-4">
      <Row className="mb-4">
        <Col>
          <h2>Comptes Gemini</h2>
          <p className="text-muted">Gérer les comptes Google pour l'accès à Gemini CLI</p>
        </Col>
        <Col xs="auto">
          <Button variant="primary" onClick={handleCreate}>
            + Ajouter un Compte
          </Button>
        </Col>
      </Row>

      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

      <Card>
        <Card.Body>
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
            </div>
          ) : (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Label</th>
                  <th>Tier</th>
                  <th>Modèles Disponibles</th>
                  <th>Usage Quotidien</th>
                  <th>Usage Minute</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {accounts.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center text-muted py-4">
                      Aucun compte Gemini configuré
                    </td>
                  </tr>
                ) : (
                  accounts.map((account) => (
                    <tr key={account.id}>
                      <td>{account.email}</td>
                      <td>{account.label}</td>
                      <td>{getTierBadge(account.tier)}</td>
                      <td>
                        {account.available_models && account.available_models.length > 0 ? (
                          <span className="text-muted" style={{fontSize: '0.85em'}}>
                            {account.available_models.slice(0, 2).join(', ')}
                            {account.available_models.length > 2 && ` +${account.available_models.length - 2}`}
                          </span>
                        ) : (
                          <span className="text-muted fst-italic">Aucun</span>
                        )}
                      </td>
                      <td>
                        {account.daily_requests_used} / {account.daily_request_limit}
                      </td>
                      <td>
                        <small className="text-muted">Limite: {account.minute_request_limit}/min</small>
                      </td>
                      <td>
                        <Badge bg={account.is_active ? 'success' : 'danger'}>
                          {account.is_active ? 'Actif' : 'Inactif'}
                        </Badge>
                        {account.needs_reauth && (
                          <Badge bg="warning" className="ms-1">Auth requise</Badge>
                        )}
                      </td>
                      <td>
                        <Button
                          variant={account.needs_reauth ? 'warning' : 'outline-warning'}
                          size="sm"
                          className="me-2"
                          onClick={() => setAuthAccount(account)}
                        >
                          Authentifier
                        </Button>
                        <Button
                          variant="outline-success"
                          size="sm"
                          className="me-2"
                          onClick={() => handleTestConnection(account)}
                          disabled={testingAccount === account.id}
                        >
                          {testingAccount === account.id ? (
                            <>
                              <Spinner animation="border" size="sm" className="me-1" />
                              Test...
                            </>
                          ) : (
                            'Tester'
                          )}
                        </Button>
                        <Button
                          variant="outline-info"
                          size="sm"
                          className="me-2"
                          onClick={() => handleRefreshModels(account)}
                          disabled={refreshingModels === account.id}
                        >
                          {refreshingModels === account.id ? (
                            <>
                              <Spinner animation="border" size="sm" className="me-1" />
                              Refresh...
                            </>
                          ) : (
                            'Rafraîchir'
                          )}
                        </Button>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="me-2"
                          onClick={() => handleEdit(account)}
                        >
                          Modifier
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDelete(account)}
                        >
                          Supprimer
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <GeminiAccountForm
        show={showForm}
        onHide={() => setShowForm(false)}
        editingAccount={editingAccount}
        formData={formData}
        setFormData={setFormData}
        onSubmit={handleSubmit}
      />

      <DeviceCodeAuthModal
        show={!!authAccount}
        onHide={() => setAuthAccount(null)}
        account={authAccount}
        apiBasePath="/admin/api/v1/gemini-accounts"
        onSuccess={() => {
          setSuccess('Authentification réussie')
          fetchAccounts()
        }}
      />
    </Container>
  )
}

export default GeminiAccounts
